import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Settings } from 'lucide-react';
import LocalFilterPanel from '../../../components/common/LocalFilterPanel';
import DateRangePresets from './DateRangePresets';
import { createEmptyLocalFilters } from '../../../hooks/useLocalFilters';

/**
 * TabFilterModal - Assigns filters to a single dashboard tab.
 *
 * Every tile in the tab (present or future) automatically uses these filters
 * instead of the app-wide global filters (replace, not merge) — see
 * stores/GlobalFilterContext/TabFilterOverrideContext.jsx for how that's wired.
 *
 * @param {object} tab - The tab being configured ({ id, label, tabFilters })
 * @param {string} activeFile - Current active file
 * @param {Function} onSave - Called with the new tabFilters object on Save
 * @param {Function} onClose - Called to dismiss without saving
 */
const TabFilterModal = ({ tab, activeFile, onSave, onClose }) => {
  const { t } = useTranslation('filter');

  const [draft, setDraft] = useState(() => ({
    ...createEmptyLocalFilters(),
    ...(tab.tabFilters || {}),
  }));

  const updateFilter = (filterType, value) => {
    setDraft((prev) => ({ ...prev, [filterType]: value }));
  };

  const handleSave = () => onSave(draft);
  const handleClearAll = () => setDraft(createEmptyLocalFilters());

  return (
    <div className="fixed inset-0 bg-black/50 flex items-start justify-center z-50 pt-24 pb-6 px-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[calc(100vh-7.5rem)] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b">
          <div className="flex items-center space-x-2">
            <Settings className="h-5 w-5 text-blue-600" />
            {/* tab.label is user data (renamable tab title) — interpolated, never translated */}
            <h2 className="text-xl font-semibold">{t('modal.title', { tabLabel: tab.label })}</h2>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            aria-label={t('common:actions.close')}
          >
            <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="p-6 space-y-6">
          <p className="text-xs text-gray-500">{t('modal.hint')}</p>

          <DateRangePresets value={draft.dateRange} onChange={(value) => updateFilter('dateRange', value)} />

          <LocalFilterPanel
            localFilters={draft}
            updateLocalFilter={updateFilter}
            activeFile={activeFile}
            dateRangeShowTime
          />
        </div>

        <div className="flex items-center justify-between p-6 border-t">
          <button onClick={handleClearAll} className="text-sm text-red-500 hover:text-red-700">
            {t('modal.resetAll')}
          </button>
          <div className="flex gap-3">
            <button onClick={onClose} className="px-4 py-2 text-sm border rounded-md hover:bg-gray-50">
              {t('common:actions.cancel')}
            </button>
            <button
              onClick={handleSave}
              className="px-4 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700"
            >
              {t('common:actions.save')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TabFilterModal;
